// Drawer «Invitar persona» de Administración · Equipo y accesos (handoff
// §3): Correos (varios, separados por coma; todos reciben el mismo rol y
// acceso), Rol como lista `prov-option` con los roles de la receta en su
// orden (los que el caller no puede otorgar van deshabilitados con la
// nota «Solo lo crea BitácoraPro»), el selector de {zona o tiendas} cuando
// el rol tiene alcance por tienda, y la marca cuando aplica (la del
// gerente de marca, ADR-0164; la de afiliación del supervisor, ADR-0165).
// Las reglas del formulario son las de routes/Equipo.tsx (la pantalla que
// sigue sirviendo a postventa/arriendos): jefe = exactamente una tienda;
// supervisor = por defecto todas las tiendas sin supervisor de su marca.
//
// Un POST /organizations/me/invitations por correo, en secuencia, con el
// mismo payload que la pantalla vieja. Si alguno falla, el drawer sigue
// abierto con el error por correo y en el campo quedan sólo los que
// faltan. El campo «Mensaje» del handoff no existe en el correo que manda
// auth-svc (sendInvitationEmail no tiene ese campo), así que no se
// muestra.

import { useMemo, useRef, useState, type KeyboardEvent } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import {
  isAssetScopedInviteRole,
  isBrandScopedRole,
  type AssignableAssetsResponse,
  type OrgSelfServiceInvitationCreateRequest,
  type RecipeMeResponse,
  type Role,
} from "@bitacorapro/api-types";
import { brandIdentityOf } from "@bitacorapro/core-engine";
import { Drawer } from "../../../components/Drawer";
import { Icon } from "../../../components/icons";
import { InviteBrandField } from "../../../components/InviteBrandField";
import { QueryBoundary } from "../../../components/QueryBoundary";
import { StorePicker } from "../../../components/StorePicker";
import { ApiError } from "../../../lib/api/error";
import { useCreateTeamInvitation } from "../../../lib/api/queries";
import { StorePickerSkeleton } from "../../../routes/EquipoSkeleton";
import { Field } from "../activos/form";
import {
  AFFILIATION_FIELD_ERROR_CODES,
  AFFILIATION_HINT,
  BRAND_FIELD_ERROR_CODES,
  INVITE_ERROR_MESSAGES,
  inviteErrorMessage,
  ONLY_BITACORAPRO,
  ONLY_BITACORAPRO_INLINE,
  parseEmails,
  roleOptions,
  type EquipoWords,
  type InviteOutcome,
} from "./model";

export type InvitarDrawerProps = {
  recipe: RecipeMeResponse;
  words: EquipoWords;
  inviterRole: Role | null;
  platformAdmin: boolean;
  brandCount: number | null;
  /** Las marcas de la org (GET /organizations/me.brands). */
  orgBrands: { id: string; name: string }[] | undefined;
  /** GET /assets/assignable: la fuente del selector. */
  storesQuery: UseQueryResult<AssignableAssetsResponse>;
  onClose: () => void;
  /** Al terminar la ronda de envíos, con o sin errores. */
  onDone: (outcome: InviteOutcome) => void;
};

export function InvitarDrawer({
  recipe,
  words: w,
  inviterRole,
  platformAdmin,
  brandCount,
  orgBrands,
  storesQuery,
  onClose,
  onDone,
}: InvitarDrawerProps) {
  const options = useMemo(
    () => roleOptions(recipe, inviterRole, { platformAdmin, brandCount }),
    [recipe, inviterRole, platformAdmin, brandCount]
  );
  // Un rol de marca necesita la lista de marcas para su selector: sin
  // ella (core-api anterior) se ofrece deshabilitado, no roto.
  const offered = useMemo(
    () =>
      options.map((o) =>
        isBrandScopedRole(o.key) && orgBrands === undefined ? { ...o, grantable: false } : o
      ),
    [options, orgBrands]
  );
  const firstGrantable = offered.find((o) => o.grantable)?.key ?? null;

  const [emailsText, setEmailsText] = useState("");
  const [emailsTouched, setEmailsTouched] = useState(false);
  const [role, setRole] = useState<Role | null>(firstGrantable);
  const [assetIdsOverride, setAssetIdsOverride] = useState<string[] | null>(null);
  const [brandId, setBrandId] = useState("");
  const [brandError, setBrandError] = useState<string | null>(null);
  const [affiliationId, setAffiliationId] = useState("");
  const [affiliationError, setAffiliationError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [perEmail, setPerEmail] = useState<InviteOutcome["failed"]>([]);
  const [sending, setSending] = useState(false);
  const brandRef = useRef<HTMLSelectElement | null>(null);
  const affiliationRef = useRef<HTMLSelectElement | null>(null);
  const roleListRef = useRef<HTMLDivElement | null>(null);
  const create = useCreateTeamInvitation();

  const parsed = parseEmails(emailsText);
  const emailsError =
    emailsTouched && parsed.invalid.length > 0
      ? `Revisa ${parsed.invalid.length === 1 ? "este correo" : "estos correos"}: ${parsed.invalid.join(", ")}`
      : null;

  const assetScope = role !== null && isAssetScopedInviteRole(role);
  const brandScope = role !== null && isBrandScopedRole(role);
  const multiBrand = (orgBrands?.length ?? 0) > 1;
  const affiliationScope =
    role !== null && brandIdentityOf(recipe, role) === "affiliation" && multiBrand;
  const single = role === "store_manager";

  const pickerStores = useMemo(() => {
    const all = storesQuery.data?.assets ?? [];
    if (!affiliationScope) return all;
    return affiliationId === "" ? [] : all.filter((a) => a.brandId === affiliationId);
  }, [storesQuery.data, affiliationScope, affiliationId]);

  // Por defecto, un supervisor cubre las tiendas sin supervisor (lo mismo
  // que calcula el servidor cuando assetIds no viaja); un jefe parte sin
  // nada elegido.
  const defaultAssetIds = useMemo(
    () =>
      role === "facility_supervisor"
        ? pickerStores.filter((a) => !a.hasSupervisor).map((a) => a.id)
        : [],
    [role, pickerStores]
  );
  const selectedAssetIds = assetIdsOverride ?? defaultAssetIds;

  const canSend =
    !sending && role !== null && parsed.valid.length > 0 && parsed.invalid.length === 0;

  function pickRole(next: Role) {
    setRole(next);
    setAssetIdsOverride(null);
    setBrandId("");
    setBrandError(null);
    setAffiliationId("");
    setAffiliationError(null);
    setFormError(null);
  }

  // Patrón radiogroup (precedente ProviderAssignForm): un solo tab stop
  // sobre la opción marcada y las flechas mueven foco y marca entre las
  // opciones otorgables; Home/End van a los extremos.
  function onRoleListKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (sending) return;
    const grantable = offered.filter((o) => o.grantable);
    if (grantable.length === 0) return;
    const i = grantable.findIndex((o) => o.key === role);
    let next: number;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      next = (Math.max(i, 0) + 1) % grantable.length;
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      next = (Math.max(i, 0) - 1 + grantable.length) % grantable.length;
    } else if (e.key === "Home") {
      next = 0;
    } else if (e.key === "End") {
      next = grantable.length - 1;
    } else {
      return;
    }
    e.preventDefault();
    const target = grantable[next]!;
    pickRole(target.key);
    roleListRef.current?.querySelector<HTMLElement>(`[data-role-key="${target.key}"]`)?.focus();
  }

  function payloadFor(email: string, r: Role): OrgSelfServiceInvitationCreateRequest {
    return {
      email,
      role: r,
      ...(assetScope && selectedAssetIds.length > 0 ? { assetIds: selectedAssetIds } : {}),
      ...(brandScope ? { scopeBrandId: brandId } : {}),
      ...(affiliationScope ? { affiliationBrandId: affiliationId } : {}),
    };
  }

  async function send() {
    setEmailsTouched(true);
    setFormError(null);
    setPerEmail([]);
    if (role === null || !canSend) return;
    if (affiliationScope && affiliationId === "") {
      setAffiliationError(INVITE_ERROR_MESSAGES.AFFILIATION_REQUIRED ?? null);
      affiliationRef.current?.focus();
      return;
    }
    if (role === "facility_supervisor" && selectedAssetIds.length === 0) {
      setFormError(`Selecciona al menos una ${w.assetOneLower} para el supervisor.`);
      return;
    }
    if (single && selectedAssetIds.length !== 1) {
      setFormError(
        `Selecciona exactamente una ${w.assetOneLower} para el jefe de ${w.assetOneLower}.`
      );
      return;
    }
    if (brandScope && brandId === "") {
      setBrandError(INVITE_ERROR_MESSAGES.BRAND_REQUIRED ?? null);
      brandRef.current?.focus();
      return;
    }
    setSending(true);
    const outcome: InviteOutcome = { sent: [], failed: [] };
    try {
      for (const email of parsed.valid) {
        try {
          await create.mutateAsync(payloadFor(email, role));
          outcome.sent.push(email);
        } catch (err) {
          const code = err instanceof ApiError ? err.code : "";
          if (brandScope && BRAND_FIELD_ERROR_CODES.has(code)) {
            setBrandError(INVITE_ERROR_MESSAGES[code] ?? null);
          } else if (affiliationScope && AFFILIATION_FIELD_ERROR_CODES.has(code)) {
            setAffiliationError(INVITE_ERROR_MESSAGES[code] ?? null);
          }
          outcome.failed.push({ email, message: inviteErrorMessage(err) });
        }
      }
    } finally {
      setSending(false);
    }
    onDone(outcome);
    if (outcome.failed.length === 0) {
      onClose();
      return;
    }
    // Quedan en el campo sólo los que fallaron: reintentar no duplica.
    setEmailsText(outcome.failed.map((f) => f.email).join(", "));
    setPerEmail(outcome.failed);
  }

  return (
    <Drawer
      title="Invitar persona"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            title="Cerrar sin invitar"
            disabled={sending}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            title="Enviar el correo de invitación a cada dirección ingresada"
            disabled={!canSend}
            onClick={() => void send()}
          >
            {sending ? "Enviando…" : "Enviar invitación"}
          </button>
        </>
      }
    >
      <Field
        label="Correos"
        hint="Separa varios correos con coma. Todos reciben el mismo rol y acceso."
        error={emailsError}
      >
        {(ctl) => (
          <textarea
            {...ctl}
            className="textarea"
            rows={2}
            value={emailsText}
            onChange={(e) => setEmailsText(e.target.value)}
            onBlur={() => setEmailsTouched(true)}
            disabled={sending}
          />
        )}
      </Field>

      <div className="field ag-field">
        <span className="field__label" id="invitar-rol-label">
          Rol
        </span>
        <div
          ref={roleListRef}
          className="prov-list"
          role="radiogroup"
          aria-labelledby="invitar-rol-label"
          onKeyDown={onRoleListKeyDown}
        >
          {offered.map((o) => {
            const checked = role === o.key;
            const focusable = checked || (role === null && o.key === firstGrantable);
            return (
              <button
                key={o.key}
                type="button"
                role="radio"
                aria-checked={checked}
                tabIndex={focusable ? 0 : -1}
                data-role-key={o.key}
                disabled={!o.grantable || sending}
                title={
                  o.grantable
                    ? `Invitar con el rol ${o.label}`
                    : `${o.label}: ${ONLY_BITACORAPRO_INLINE}`
                }
                className={`prov-option${checked ? " prov-option--selected" : ""}${
                  o.grantable ? "" : " ag-disabled"
                }`}
                onClick={() => o.grantable && pickRole(o.key)}
              >
                <span className="prov-option__meta">
                  <span className="prov-option__name">{o.label}</span>
                  <span className="ag-muted ag-dblock">
                    {o.sees}
                    {!o.grantable && ` · ${ONLY_BITACORAPRO}`}
                  </span>
                </span>
                {checked && (
                  <span className="prov-option__check" aria-hidden="true">
                    <Icon.Check s={11} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {brandScope && orgBrands && (
        <InviteBrandField
          ref={brandRef}
          brands={orgBrands}
          value={brandId}
          onChange={(v) => {
            setBrandId(v);
            if (v !== "") setBrandError(null);
          }}
          error={brandError}
          idPrefix="invitar"
        />
      )}
      {affiliationScope && orgBrands && (
        <InviteBrandField
          ref={affiliationRef}
          brands={orgBrands}
          value={affiliationId}
          onChange={(v) => {
            setAffiliationId(v);
            setAssetIdsOverride(null);
            if (v !== "") setAffiliationError(null);
          }}
          error={affiliationError}
          idPrefix="invitar-afiliacion"
          label={`${w.brand} del supervisor`}
          hint={AFFILIATION_HINT}
        />
      )}

      {assetScope && (
        <QueryBoundary
          query={storesQuery}
          label={`las ${w.assetManyLower}`}
          kind="section"
          skeleton={<StorePickerSkeleton />}
          isEmpty={(data) => data.assets.length === 0}
          empty={
            <div className="store-picker store-picker--empty">
              <div className="store-picker__legend">
                No tienes {w.assetManyLower} asignables todavía.
              </div>
            </div>
          }
        >
          {() =>
            affiliationScope && affiliationId === "" ? (
              <span className="cell-meta">
                Elige primero la {w.brandLower} del supervisor para ver sus {w.assetManyLower}.
              </span>
            ) : pickerStores.length === 0 ? (
              <div className="store-picker store-picker--empty">
                <div className="store-picker__legend">
                  Esta {w.brandLower} no tiene {w.assetManyLower} asignables dentro de tu alcance.
                </div>
              </div>
            ) : (
              <StorePicker
                stores={pickerStores}
                selectedIds={selectedAssetIds}
                onChange={(next) => setAssetIdsOverride(next)}
                disabled={sending}
                mode={single ? "single" : "multi"}
                legend={`${w.groupOne} o ${w.assetManyLower}`}
              />
            )
          }
        </QueryBoundary>
      )}

      {formError && (
        <p className="auth-error ag-mt8" role="alert">
          {formError}
        </p>
      )}
      {perEmail.length > 0 && (
        <ul className="auth-error ag-mt8" role="alert">
          {perEmail.map((f) => (
            <li key={f.email}>
              <span className="mono">{f.email}</span> · {f.message}
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
}
